import { useState, useCallback } from 'react';

const STORAGE_KEY = 'swiso_process_v3';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return { role: null, progress: {}, wizNode: 'root', wizPath: [] };
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {}
}

export function useAppState() {
  const [state, setState] = useState(() => loadState());

  const update = useCallback((patch) => {
    setState(prev => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      saveState(next);
      return next;
    });
  }, []);

  const setRole = useCallback((role) => {
    update({ role, wizNode: 'root', wizPath: [] });
  }, [update]);

  const setStep = useCallback((sectionKey, stepIndex, field, value) => {
    update(prev => {
      const progress = { ...prev.progress };
      if (!progress[sectionKey]) progress[sectionKey] = {};
      progress[sectionKey] = { ...progress[sectionKey] };
      const existing = progress[sectionKey][stepIndex] || { status: 'Not Started', date: '', notes: '', evidence: '' };
      progress[sectionKey][stepIndex] = { ...existing, [field]: value };
      return { ...prev, progress };
    });
  }, [update]);

  const wizSelect = useCallback((nextNode, result) => {
    update(prev => ({
      ...prev,
      wizPath: [...prev.wizPath, prev.wizNode],
      wizNode: result ? '__result__' : nextNode,
      wizResult: result || null,
    }));
  }, [update]);

  const wizBack = useCallback(() => {
    update(prev => {
      const path = [...prev.wizPath];
      const node = path.pop();
      return { ...prev, wizPath: path, wizNode: node || 'root', wizResult: null };
    });
  }, [update]);

  const wizReset = useCallback(() => {
    update(prev => ({ ...prev, wizNode: 'root', wizPath: [], wizResult: null }));
  }, [update]);

  const clearProgress = useCallback(() => {
    update(prev => ({ ...prev, progress: {} }));
  }, [update]);

  return { state, setRole, setStep, wizSelect, wizBack, wizReset, update, clearProgress };
}
